import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useAppStore } from '@/store/app-store'
import { useUserStore } from '@/store/user-store/user-store'
import { timer } from '@/utils/helpers'
import { useAPI } from '@/utils/requests/api-plugin'

import {
    getPromoCodePlatform,
    isPromoCodeLocked,
    getRejection,
    isRejectedCode,
    type PromoCodeRejection,
    type PromoCodeLockState,
    registerFailedAttempt,
} from './promo-code-rules'

export type PromoCodeResult = 'success' | 'unsynced' | PromoCodeRejection | 'locked' | 'error'

const USER_SYNC_DELAYS_MS = [0, 1000, 3000]

export const usePromoCodeStore = defineStore(
    'promo-code',
    () => {
        const api = useAPI()
        const appStore = useAppStore()
        const userStore = useUserStore()

        const isModalOpened = ref(false)
        const modalTrigger = ref('')
        const lock = ref<PromoCodeLockState>({ failedAttempts: 0, lockedUntil: null })
        const isUnsynced = ref(false)
        let pending: Promise<PromoCodeResult> | null = null

        function isLocked() {
            return isPromoCodeLocked(lock.value, Date.now())
        }

        async function openModal(trigger: string) {
            try {
                await userStore.authorizeUser(trigger)
            } catch {
                return
            }
            modalTrigger.value = trigger
            isModalOpened.value = true
        }

        function closeModal() {
            isModalOpened.value = false
        }

        // the code is already applied, so only the profile read is retried: the header and the
        // ad switch-off follow user.adsDisabledUntil
        async function syncUser(): Promise<'success' | 'unsynced'> {
            for (const delayMs of USER_SYNC_DELAYS_MS) {
                await timer(delayMs)
                if (await userStore.updateUserSilently()) {
                    isUnsynced.value = false
                    return 'success'
                }
            }
            isUnsynced.value = true
            return 'unsynced'
        }

        // one request at a time; a reopened popup or a purchase hand-off awaits the one in flight
        function track(request: Promise<PromoCodeResult>): Promise<PromoCodeResult> {
            const tracked = request.finally(() => {
                if (pending === tracked) {
                    pending = null
                }
            })
            pending = tracked
            return tracked
        }

        function redeem(code: string): Promise<PromoCodeResult> {
            return track(send(code))
        }

        // the code is already applied: only the profile is re-read, the redeem is never sent again
        function retrySync(): Promise<PromoCodeResult> {
            return track(syncUser())
        }

        async function send(code: string): Promise<PromoCodeResult> {
            if (isLocked()) {
                return 'locked'
            }

            const platform = getPromoCodePlatform({
                isNativeIos: appStore.isNativeIosEnv,
                isNativeAndroid: appStore.isNativeAndroidEnv,
                isNativeApp: appStore.isNativeAppEnv,
            })
            const result = await api.redeemPromoCode(code, platform)

            if (result.status === 'ok') {
                lock.value = { failedAttempts: 0, lockedUntil: null }
                return syncUser()
            }

            if (!isRejectedCode(result)) {
                return 'error'
            }

            lock.value = registerFailedAttempt(lock.value, Date.now())
            return isLocked() ? 'locked' : getRejection(result)
        }

        return {
            isModalOpened,
            modalTrigger,
            lock,
            isUnsynced,
            isLocked,
            getPending: () => pending,
            openModal,
            closeModal,
            redeem,
            retrySync,
        }
    },
    {
        persist: {
            key: 'promo_code',
            pick: ['lock'],
        },
    },
)
